/**
 * "Look up a company" - fetch one company from Companies House on demand,
 * outside any SIC run.
 *
 * A SIC sweep is a blunt instrument. Someone who already knows the one company
 * they want should not have to acquire its entire code to see it, and someone
 * checking whether a company is in the registry at all needs an answer that does
 * not depend on which codes have been swept.
 *
 * Two shapes of answer, because there are two shapes of question. A company
 * **number** is unambiguous: it is fetched, stored and opened. A **name** is
 * not, so the API returns candidates and imports nothing until one is chosen -
 * typing "smith" must not put twenty companies in the registry that no
 * acquisition accounts for.
 */

import { useState } from 'react';
import { companiesApi } from '../lib/api.js';
import { IconAlert, IconClose, IconExternal, IconPlus, IconSearch, IconSpinner } from './Icons.jsx';

export default function DirectLookup({ onImported, onClose }) {
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [candidates, setCandidates] = useState(null);
  const [imported, setImported] = useState(null);

  const run = async (term) => {
    const trimmed = term.trim();
    if (trimmed.length < 2) return;

    setBusy(true);
    setError(null);
    setImported(null);

    try {
      const result = await companiesApi.lookup(trimmed);

      if (result.kind === 'company') {
        setCandidates(null);
        setImported(result);
        // The page selects it, so the profile pane opens on what was just
        // fetched rather than making the operator find it in the list.
        onImported?.(result.company.companyNumber, result.outcome);
      } else {
        setCandidates(result);
      }
    } catch (caught) {
      setError(caught.message ?? 'Could not reach Companies House.');
      setCandidates(null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="lookup-panel">
      <div className="lookup-panel-header">
        <span className="card-title">Look up a company</span>
        <button type="button" className="btn btn-ghost btn-sm" onClick={onClose} aria-label="Close the lookup box">
          <IconClose />
        </button>
      </div>

      <form
        className="row"
        onSubmit={(event) => {
          event.preventDefault();
          void run(query);
        }}
      >
        <div className="search-field" style={{ flex: 1 }}>
          <span className="search-field-icon">
            <IconSearch />
          </span>
          <input
            className="input"
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Company number, or a company name"
            aria-label="Company number or name"
            autoFocus
          />
        </div>
        <button type="submit" className="btn btn-primary" disabled={busy || query.trim().length < 2}>
          {busy ? <IconSpinner /> : null}
          Fetch
        </button>
      </form>

      <p className="text-xs text-faint">
        Spends one Companies House call from the 600-per-5-minutes budget. A company number is
        fetched and stored straight away; a name comes back as a list to choose from.
      </p>

      {error ? (
        <div className="alert alert-error">
          <span className="alert-icon">
            <IconAlert />
          </span>
          <span className="alert-body">
            <span className="alert-title">Could not look that up</span>
            {error}
          </span>
        </div>
      ) : null}

      {imported ? (
        <div className="alert alert-ok">
          <span className="alert-body">
            <span className="alert-title">
              {imported.outcome === 'new' ? 'Added to the registry' : 'Refreshed'}
            </span>
            {imported.company.name} ({imported.company.companyNumber}) is open on the right.
            {imported.outcome === 'updated'
              ? ' It was already held, and this replaced it with what Companies House holds now.'
              : ''}
          </span>
        </div>
      ) : null}

      {candidates ? (
        <div className="lookup-candidates">
          <p className="text-sm text-dim">
            {candidates.hits.toLocaleString()} companies match &ldquo;{candidates.term}&rdquo;.
            {candidates.candidates.length < candidates.hits
              ? ` Showing the first ${candidates.candidates.length}.`
              : ''}{' '}
            Choose one to fetch and store it.
          </p>

          {candidates.candidates.length === 0 ? (
            <div className="empty">
              <h3 className="empty-title">Nothing matched that name</h3>
              <p className="empty-description">
                Companies House matches on the registered name, so a trading name often finds
                nothing. Try a shorter fragment, or paste the company number instead.
              </p>
            </div>
          ) : (
            <ul className="lookup-candidate-list">
              {candidates.candidates.map((candidate) => (
                <li className="lookup-candidate" key={candidate.companyNumber}>
                  <div className="lookup-candidate-main">
                    <span className="lookup-candidate-name">{candidate.name}</span>
                    <span className="lookup-candidate-meta">
                      <a
                        className="link-external mono"
                        href={`https://find-and-update.company-information.service.gov.uk/company/${candidate.companyNumber}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {candidate.companyNumber}
                        <IconExternal />
                      </a>
                      <span
                        className={`badge ${candidate.status === 'active' ? 'badge-ok' : 'badge-unknown'}`}
                      >
                        <span className="badge-dot" />
                        {candidate.statusLabel ?? 'Unknown'}
                      </span>
                      {candidate.incorporatedOn ? <span>inc. {candidate.incorporatedOn}</span> : null}
                    </span>
                    {candidate.address ? (
                      <span className="text-xs text-faint">{candidate.address}</span>
                    ) : null}
                    {/* Codes come back from the same call, so showing them costs
                        nothing and answers "is this the right company?" better
                        than the address does. */}
                    {candidate.sicCodes.length > 0 ? (
                      <span className="lookup-candidate-codes">
                        {candidate.sicCodes.map((entry) => (
                          <span key={entry.code} className="text-xs text-faint">
                            <span className="mono">{entry.code}</span>{' '}
                            {entry.description ?? 'not on the current list'}
                          </span>
                        ))}
                      </span>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    className="btn btn-sm"
                    disabled={busy}
                    onClick={() => void run(candidate.companyNumber)}
                  >
                    <IconPlus />
                    Fetch
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  );
}
